import React, { useEffect, useState } from "react";
import { DownloadSimple, X, Share, Plus } from "@phosphor-icons/react";

/**
 * Roll Fast TMS — PWA Install Prompt.
 *
 * Renders a small banner near the bottom of the screen prompting the user to
 * add the app to their home screen.
 *
 * Two flows are supported:
 *  1. Chromium-based browsers (Android Chrome, Edge, desktop Chrome) → uses
 *     the standard `beforeinstallprompt` event + native install dialog.
 *  2. iOS Safari → no native prompt is available; we show an explainer with
 *     the Share-button → "Add to Home Screen" instructions.
 *
 * The prompt is suppressed when:
 *  - The app is already running in standalone display mode.
 *  - The user dismissed it within the last 7 days (localStorage flag).
 *  - The app is open in an iframe (avoids the Emergent preview wrapper).
 */
const DISMISS_KEY = "rf_pwa_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const isIos = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

const inIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [showIosHelp, setShowIosHelp] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone() || inIframe()) return;
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_WINDOW_MS) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    // iOS Safari: no event — show instruction banner after a short delay.
    if (isIos()) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => { clearTimeout(t); window.removeEventListener("beforeinstallprompt", onPrompt); };
    }

    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
    setShowIosHelp(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try {
        const choice = await deferred.userChoice;
        if (choice?.outcome !== "accepted") {
          localStorage.setItem(DISMISS_KEY, String(Date.now()));
        }
      } finally {
        setDeferred(null);
        setShow(false);
      }
    } else if (isIos()) {
      setShowIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <>
      <div
        role="dialog"
        aria-label="Install Roll Fast TMS"
        data-testid="pwa-install-banner"
        className="fixed left-3 right-3 bottom-3 z-[60] sm:left-auto sm:right-4 sm:bottom-4 sm:max-w-sm"
        style={{ background: "#0A0A0A", color: "#fff", border: "1px solid #2a2a2a", borderRadius: 8, boxShadow: "0 12px 40px rgba(0,0,0,0.5)" }}
      >
        <div className="p-3 sm:p-4 flex items-start gap-3">
          <div className="shrink-0 w-10 h-10 flex items-center justify-center rounded" style={{ background: "#E50914" }}>
            <DownloadSimple size={18} weight="bold" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">Install Roll Fast TMS</div>
            <div className="text-xs text-neutral-400 mt-0.5">
              {isIos()
                ? "Add to Home Screen for a full-screen app experience."
                : "One-tap install — runs full-screen with no browser bar."}
            </div>
            <div className="mt-2 flex gap-2">
              <button
                type="button"
                onClick={install}
                data-testid="pwa-install-btn"
                className="rf-btn rf-btn-primary text-xs"
              >
                {isIos() ? "How" : "Install"}
              </button>
              <button
                type="button"
                onClick={dismiss}
                data-testid="pwa-dismiss-btn"
                className="rf-btn rf-btn-ghost text-xs"
              >
                Not now
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-neutral-500 hover:text-white p-1 -mr-1 -mt-1 shrink-0"
          >
            <X size={16} weight="bold" />
          </button>
        </div>
      </div>

      {showIosHelp && (
        <div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-3 sm:p-4"
          style={{ background: "rgba(0,0,0,0.7)" }}
          onClick={() => setShowIosHelp(false)}
        >
          <div
            role="dialog"
            aria-label="Install instructions"
            data-testid="pwa-ios-instructions"
            className="w-full sm:max-w-sm"
            style={{ background: "#0A0A0A", color: "#fff", border: "1px solid #2a2a2a", borderRadius: 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-bold text-base">Add to Home Screen</div>
                <button onClick={() => setShowIosHelp(false)} className="text-neutral-500"><X size={18} weight="bold" /></button>
              </div>
              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full" style={{ background: "#1a1a1a", color: "#fff", fontWeight: 800, fontSize: 11 }}>1</span>
                  <span>Tap the <Share size={14} className="inline" weight="bold" /> <strong>Share</strong> button in Safari&rsquo;s toolbar.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full" style={{ background: "#1a1a1a", color: "#fff", fontWeight: 800, fontSize: 11 }}>2</span>
                  <span>Scroll and choose <Plus size={14} className="inline" weight="bold" /> <strong>Add to Home Screen</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="shrink-0 w-6 h-6 flex items-center justify-center rounded-full" style={{ background: "#1a1a1a", color: "#fff", fontWeight: 800, fontSize: 11 }}>3</span>
                  <span>Tap <strong>Add</strong>. Roll Fast will appear on your home screen.</span>
                </li>
              </ol>
              <button
                type="button"
                onClick={dismiss}
                className="rf-btn rf-btn-primary text-xs w-full mt-4"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
